import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import { App, View, Panel, f7ready } from "framework7-react";

import routes from "modules/2_routes/mobile";
import { getCurrentUser } from "../current_user/actions";
import Login from "modules/auth/Login";
import BootSplash from "modules/auth/BootSplash";
import PendingSyncSheet from "modules/offline/PendingSyncSheet";
import { ensureAccessToken, SESSION_ENDED_EVENT } from "modules/auth/api";
import { clearAccessToken } from "modules/auth/token";
import { registerPushNotifications, consumePendingPushLink } from "modules/auth/pushNotifications";
import { cacheGetCurrentUser, cacheSetCurrentUser } from "modules/offline/cache";
import { hydrateStoreFromCache } from "modules/offline/hydrate";
import { syncMobileLookups } from "modules/offline/preloadLookups";
import { runSync } from "modules/offline";
import { prefetchCurrentProject } from "modules/offline/prefetch";
import { mobileStore } from "./store";
import { GET_CURRENT_USER } from "modules/current_user/actions";

const mapStateToProps = (state) => ({
  current_user: state.current_user,
});

const f7params = {
  name: "LionV3",
  id: "lionv3.app",
  theme: "md",
  routes: routes,
  popover: { backdrop: false }, // leftover .popover-backdrop-in blacks the view
};

const bootFromCache = async () => {
  const cachedUser = await cacheGetCurrentUser();
  // ponytail: cached user is enough to browse offline; refresh may be missing if an
  // earlier offline refresh attempt wiped it (fixed) — sync needs a later online login
  if (_.isEmpty(cachedUser)) return false;

  mobileStore.dispatch({
    type: `${GET_CURRENT_USER}_FULFILLED`,
    payload: { data: cachedUser },
  });
  await hydrateStoreFromCache(mobileStore);
  return true;
};

const F7WindowBind = () => {
  useEffect(() => {
    f7ready((f7) => {
      window.f7 = f7;
    });
  }, []);
  return null;
};

// ponytail: Secure Storage / LAN API can hang; after this we show login instead of spinning forever
const withTimeout = (promise, ms) => {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error("timeout")), ms);
    }),
  ]).finally(() => clearTimeout(timer));
};

const Router = (props) => {
  const { current_user } = props;
  const [rerenderKey, setRerenderKey] = React.useState(Date.now());
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      try {
        const token = await withTimeout(ensureAccessToken(), 10000).catch(() => null);
        if (cancelled) return;

        if (!token) {
          // Refresh failed (often offline). Don't trust isOnline() at boot — use cached session.
          if (await withTimeout(bootFromCache(), 8000).catch(() => false)) {
            setAuthed(true);
            syncMobileLookups(mobileStore, { force: true });
            prefetchCurrentProject(mobileStore, { force: true });
            return;
          }
          setAuthed(false);
          return;
        }

        setAuthed(true);
        try {
          await withTimeout(props.getCurrentUser(), 10000);
          if (cancelled) return;
          if (_.isEmpty(mobileStore.getState().current_user)) {
            if (await withTimeout(bootFromCache(), 8000).catch(() => false)) return;
            clearAccessToken();
            setAuthed(false);
            return;
          }
          syncMobileLookups(mobileStore, { force: true });
          prefetchCurrentProject(mobileStore, { force: true });
          runSync();
          registerPushNotifications();
        } catch (_e) {
          if (await withTimeout(bootFromCache(), 8000).catch(() => false)) {
            syncMobileLookups(mobileStore, { force: true });
            prefetchCurrentProject(mobileStore, { force: true });
            return;
          }
          clearAccessToken();
          setAuthed(false);
        }
      } catch (_e) {
        setAuthed(false);
      } finally {
        if (!cancelled) setReady(true);
      }
    };
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hard auth failure → show Login without full reload (avoids boot sync kick loop)
  useEffect(() => {
    const onSessionEnded = () => {
      setAuthed(false);
      setReady(true);
    };
    window.addEventListener(SESSION_ENDED_EVENT, onSessionEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onSessionEnded);
  }, []);

  const onLoginSuccess = async () => {
    setAuthed(true);
    await props.getCurrentUser();
    const user = mobileStore.getState().current_user;
    if (!_.isEmpty(user)) {
      await cacheSetCurrentUser(user);
    }
    syncMobileLookups(mobileStore, { force: true });
    prefetchCurrentProject(mobileStore, { force: true });
    runSync();
    registerPushNotifications();
    setReady(true);
  };

  if (!ready) {
    return <BootSplash />;
  }

  if (!authed || _.isEmpty(current_user)) {
    // .capacitor-login scopes the web Bootstrap theme to this screen (capacitor/login_theme.scss)
    return (
      <div className="capacitor-login">
        <Login onSuccess={onLoginSuccess} />
      </div>
    );
  }

  return (
    <App
      {...f7params}
      on={{
        init(f7) {
          window.f7 = f7;
          consumePendingPushLink();
        },
      }}
      onRouteChange={(newRoute) => {
        const page = newRoute.url || newRoute.path;
        if (page && !_.startsWith(page, "/panel-left")) {
          props.getCurrentUser(page);
        }
      }}
    >
      <F7WindowBind />
      <Panel
        left
        cover
        resizable
        onPanelOpen={() => {
          setRerenderKey(Date.now());
        }}
      >
        <View key={rerenderKey} url="/panel-left/" linksView=".view-main" reloadPages />
      </Panel>
      <View
        main
        url={window.location.pathname + window.location.search}
        browserHistory
        browserHistorySeparator=""
        browserHistoryInitialMatch={false}
      />
      <PendingSyncSheet />
    </App>
  );
};

export default connect(mapStateToProps, { getCurrentUser })(Router);
